// Mi perfil — OwnProfileView's identity block + EditProfileSheet (shared/API.md §10.6–10.7), inline.
// The fields open with SERVER truth, never the local row: §10.6's empty-string-clears rule makes a
// stale prefill a data-loss bug, so until /users/me answers the fields are disabled, not blank.
// The photo is downscaled to ≤512 px JPEG at PICK time (AvatarImagePrep): a file the browser cannot
// decode is refused right there, never as a mystery upload failure later.
import { useEffect, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import { userDisplayName } from '../../../core/channelListDerivation';
import { Icon } from '../../../design/icons';
import { t } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { useLive } from '../../hooks';
import { Button, InlineStatus } from '../../kit/Kit';
import { PresenceDot, SaveStatus, SettingsGroup, useAccount, useSaver } from './parts';

/** Avatar bytes → ≤`maxPixel` JPEG (AvatarImagePrep.jpegData). null = not decodable as an image. */
export async function avatarJpeg(file: Blob, maxPixel = 512): Promise<Blob | null> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return null;
  }
  const scale = Math.min(1, maxPixel / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const g = canvas.getContext('2d');
  if (!g) return null;
  g.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.85));
}

type Fields = { firstName: string; lastName: string; nickname: string; position: string };

export function ProfileSection() {
  const { account, loadError, reload, patch } = useAccount();
  const { value: local } = useLive(() => data.call('accountLocal'), ['users', 'userStatuses'], []);
  const user = local?.user ?? null;
  const status = local?.status ?? account?.status ?? 'offline';

  // MARK: photo
  const fileInput = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const photo = useSaver();
  useEffect(() => () => void (preview && URL.revokeObjectURL(preview)), [preview]);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    const jpeg = await avatarJpeg(file);
    if (!jpeg) {
      photo.run(async () => false, undefined, t('web.settings.photo.notImage'));
      return;
    }
    const url = URL.createObjectURL(jpeg);
    setPreview(url);
    photo.run(
      async () => {
        const ok = await data.call('accountUploadAvatar', jpeg);
        if (ok) setPreview(null);
        return ok;
      },
      () => setPreview(null),
      t('web.settings.photo.failed'),
    );
  };
  const remove = () => photo.run(() => data.call('accountRemoveAvatar'), undefined, t('web.settings.photo.failed'));

  // MARK: fields
  const server: Fields | null = account ? { firstName: account.user.firstName, lastName: account.user.lastName, nickname: account.user.nickname, position: account.user.position } : null;
  const [fields, setFields] = useState<Fields | null>(server);
  useEffect(() => {
    if (server && !fields) setFields(server);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);
  const dirty = !!fields && !!server && (Object.keys(fields) as Array<keyof Fields>).some((k) => fields[k] !== server[k]);
  const details = useSaver();
  const save = () => {
    if (!fields) return;
    const sent = { ...fields };
    details.run(async () => {
      const r = await data.call('accountUpdateProfile', sent);
      if (r.ok && account) patch({ user: { ...account.user, ...sent } });
      return r.ok;
    }, undefined, t('profile.edit.saveFailed'));
  };
  const set = (k: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement>) => setFields((f) => (f ? { ...f, [k]: e.target.value } : f));

  const name = user ? userDisplayName(user) : account ? `${account.user.firstName} ${account.user.lastName}`.trim() || account.user.username : '';
  const hasPhoto = (user?.lastPictureUpdate ?? account?.user.lastPictureUpdate ?? 0) > 0;
  const busy = photo.state.kind === 'saving';

  return (
    <>
      <SettingsGroup>
        <div className="st-identity">
          <div className={`st-avatar${busy ? ' busy' : ''}`}>
            {preview ? (
              <img src={preview} alt="" className="st-avatar-preview" />
            ) : (
              <Avatar key={user?.lastPictureUpdate ?? 0} userId={user?.id ?? account?.user.id ?? ''} name={name || '?'} size={72} version={user?.lastPictureUpdate ?? 0} />
            )}
            <button type="button" className="st-avatar-badge" onClick={() => fileInput.current?.click()} title={t('web.settings.photo.change')} aria-label={t('web.settings.photo.change')} disabled={busy}>
              <Icon name="pencilLine" size={13} strokeWidth={2} />
            </button>
          </div>
          <div className="st-identity-text">
            <div className="st-identity-name">{name}</div>
            <div className="st-identity-sub">@{user?.username ?? account?.user.username ?? ''}</div>
            {account?.user.position ? <div className="st-identity-sub">{account.user.position}</div> : null}
            {status === 'ooo' ? (
              <div className="st-identity-ooo">
                <PresenceDot status="ooo" size={8} />
                {t('profile.status.ooo')}
              </div>
            ) : null}
            <div className="st-identity-actions">
              <Button icon="pencilLine" onClick={() => fileInput.current?.click()} disabled={busy}>
                {t('web.settings.photo.change')}
              </Button>
              {hasPhoto ? (
                <Button kind="quiet" icon="trash" onClick={remove} disabled={busy}>
                  {t('web.settings.photo.remove')}
                </Button>
              ) : null}
              <SaveStatus state={photo.state} />
            </div>
          </div>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              void pick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </div>
      </SettingsGroup>

      {loadError && !account ? <InlineStatus kind="error" text={t('profile.edit.loadFailed')} onRetry={reload} /> : null}

      <SettingsGroup title={t('web.settings.profile.details')} footer={t('profile.edit.footer')}>
        <div className="st-form">
          {(
            [
              ['firstName', 'profile.field.firstName', 'given-name'],
              ['lastName', 'profile.field.lastName', 'family-name'],
              ['nickname', 'profile.field.nickname', 'nickname'],
              ['position', 'profile.field.position', 'organization-title'],
            ] as Array<[keyof Fields, string, string]>
          ).map(([k, label, auto]) => (
            <label key={k} className="st-field">
              <span>{t(label)}</span>
              <input className="text-input" value={fields?.[k] ?? ''} onChange={set(k)} disabled={!fields} autoComplete={auto} maxLength={k === 'position' ? 128 : 64} placeholder={fields ? '' : '…'} />
            </label>
          ))}
          <div className="st-form-actions">
            <SaveStatus state={details.state} />
            {dirty ? (
              <Button kind="quiet" onClick={() => server && setFields(server)}>
                {t('common.cancel')}
              </Button>
            ) : null}
            <Button kind="primary" onClick={save} disabled={!dirty || details.state.kind === 'saving'}>
              {t('profile.save')}
            </Button>
          </div>
        </div>
      </SettingsGroup>

      <SettingsGroup title={t('web.settings.profile.account')}>
        <div className="list-row">
          <span className="list-row-text">
            <span className="list-row-title">{t('profile.field.username')}</span>
          </span>
          <span className="list-row-trailing st-value">{user?.username ?? account?.user.username ?? ''}</span>
        </div>
        {account?.user.email ? (
          <div className="list-row">
            <span className="list-row-text">
              <span className="list-row-title">{t('profile.field.email')}</span>
            </span>
            <span className="list-row-trailing st-value">{account.user.email}</span>
          </div>
        ) : null}
      </SettingsGroup>
    </>
  );
}
